import {
  ACCEPTED_VIDEO_MIME_TYPES,
  MAX_LISTING_VIDEOS,
  MAX_VIDEO_BYTES,
  MAX_VIDEO_SECONDS,
} from "../constants/listingMedia";

export type ListingVideoEntry = {
  key: string;
  contentType: string;
  durationSeconds?: number;
  bytes?: number;
};

export type VideoValidationResult =
  | { valid: true; durationSeconds: number; contentType: string }
  | { valid: false; reason: string };

function normalizeVideoContentType(file: File): string {
  const raw = (file.type || "").toLowerCase().trim();
  if (raw === "video/mp4" || raw === "video/webm") return raw;
  const name = file.name.toLowerCase();
  if (name.endsWith(".mp4") || name.endsWith(".m4v")) return "video/mp4";
  if (name.endsWith(".webm")) return "video/webm";
  return raw;
}

function loadVideoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    // Some browsers only fire loadedmetadata reliably when the element is attached.
    video.style.cssText =
      "position:fixed;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none";
    document.body.appendChild(video);
    const cleanup = () => {
      URL.revokeObjectURL(url);
      video.removeAttribute("src");
      video.load();
      video.remove();
    };
    video.onloadedmetadata = () => {
      const duration = Number(video.duration);
      cleanup();
      if (!Number.isFinite(duration) || duration <= 0) {
        reject(new Error("Could not read video duration."));
        return;
      }
      resolve(duration);
    };
    video.onerror = () => {
      cleanup();
      reject(new Error("Could not read this video file."));
    };
    video.src = url;
  });
}

/**
 * Validate a candidate listing video (MIME, size, duration ≤ 60s).
 */
export async function validateVideoFileAsync(
  file: File
): Promise<VideoValidationResult> {
  const contentType = normalizeVideoContentType(file);
  if (
    !(ACCEPTED_VIDEO_MIME_TYPES as readonly string[]).includes(contentType)
  ) {
    return {
      valid: false,
      reason:
        "Videos must be MP4 or WebM. iPhone users: export/share the clip as MP4 first.",
    };
  }
  if (file.size <= 0) {
    return { valid: false, reason: "Video file is empty." };
  }
  if (file.size > MAX_VIDEO_BYTES) {
    const mb = Math.ceil(file.size / (1024 * 1024));
    return {
      valid: false,
      reason: `Video is too large (${mb}MB). Maximum size is ${Math.floor(
        MAX_VIDEO_BYTES / (1024 * 1024)
      )}MB.`,
    };
  }

  try {
    const durationSeconds = await loadVideoDuration(file);
    if (durationSeconds > MAX_VIDEO_SECONDS + 0.25) {
      return {
        valid: false,
        reason: `Video is too long (${Math.ceil(
          durationSeconds
        )}s). Maximum length is ${MAX_VIDEO_SECONDS} seconds.`,
      };
    }
    return {
      valid: true,
      durationSeconds: Math.round(durationSeconds * 10) / 10,
      contentType,
    };
  } catch (err) {
    return {
      valid: false,
      reason:
        err instanceof Error
          ? err.message
          : "Could not validate this video file.",
    };
  }
}

export function canAddMoreVideos(currentCount: number): boolean {
  return currentCount < MAX_LISTING_VIDEOS;
}

export function formatVideoDuration(seconds?: number | null): string {
  if (seconds == null || !Number.isFinite(seconds)) return "";
  const total = Math.max(0, Math.round(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
